import { Injectable, signal } from '@angular/core';
import { Capacitor }  from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen }     from '@capacitor/splash-screen';
import { Keyboard, KeyboardResize, KeyboardStyle } from '@capacitor/keyboard';

/**
 * One-time bootstrap for the Capacitor native shell. Wires up:
 *  - status bar style (matches the brand)
 *  - keyboard resize behaviour (Native: prefer "Body" so the fixed navbar stays put)
 *  - keyboard show/hide hooks that scroll the focused input into view
 *  - splash screen hide once Angular is ready
 *
 * Safe to call on the web — every step short-circuits when not running natively.
 */
@Injectable({ providedIn: 'root' })
export class MobileBootstrapService {
  private initialized = false;

  /** True when running inside the Capacitor iOS/Android shell. Read once at construction. */
  readonly isNative = signal<boolean>(Capacitor.isNativePlatform());

  async init(): Promise<void> {
    if (this.initialized) { return; }
    this.initialized = true;
    if (!Capacitor.isNativePlatform()) { return; }

    // Status bar — keep brand green visible behind the iOS/Android system bar.
    try {
      await StatusBar.setStyle({ style: Style.Light });
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: '#1A7A5E' });
      }
    } catch { /* ignore — some platforms (e.g. iOS PWA) reject these */ }

    // Keyboard — resize the body (not the whole viewport) so fixed elements
    // like the top navbar don't get shoved off-screen.
    try {
      await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
      await Keyboard.setStyle({ style: KeyboardStyle.Light });
      Keyboard.addListener('keyboardWillShow', () => {
        const el = document.activeElement as HTMLElement | null;
        if (el && typeof el.scrollIntoView === 'function') {
          // Slight delay so the layout settles before we scroll.
          setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 80);
        }
      });
    } catch { /* not all platforms support every method */ }

    // Hide the splash once Angular's first paint is on-screen.
    try {
      await SplashScreen.hide({ fadeOutDuration: 250 });
    } catch { /* fine — splash plugin may not be active */ }
  }
}
